import React, { useCallback, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { api } from "../../../../api/apiClient";
import { notify } from "../../../components-ui/toast";
import {
  ServiceDto,
  ServiceEnrollmentDto,
  ServiceGroupDto,
  RegistrationMemberType,
  ServiceRoleDto,
} from "../../../types/types";
import ServiceEnrollmentMembershipCard from "./ServiceEnrollmentMembershipCard";

import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/buttons.css";
import "./ServiceEnrollmentCard.css";

interface ServiceEnrollmentCardProps {
  partyId?: number | null;
  tenantId?: number;
  selectedServices?: number[];
  onSelectedServicesChange?: (serviceIds: number[]) => void;
  selectedServiceRoles?: ServiceRoleSelections;
  onSelectedServiceRolesChange?: (roles: ServiceRoleSelections) => void;
  registrationServiceId?: number | null;
  registrationServiceName?: string;
  registrationMemberType?: RegistrationMemberType;
  readonly?: boolean;
}

export type ServiceRoleSelections = Record<number, number[]>;

const memberTypeLabel = (memberType?: string) => {
  switch (memberType) {
    case "PrimaryContact":
      return "Primary contact";
    case "Volunteer":
      return "Volunteer";
    case "Staff":
      return "Staff";
    default:
      return "Service user";
  }
};

const memberTypesLabel = (enrollment: ServiceEnrollmentDto) =>
  (enrollment.memberTypes?.length ? enrollment.memberTypes : [enrollment.memberType])
    .map(memberTypeLabel)
    .join(", ");

export const createServiceEnrollmentsForParty = async (
  partyId: number,
  serviceIds: number[],
  source = "Manual",
  selectedServiceRoles: ServiceRoleSelections = {},
  memberType: RegistrationMemberType = "ServiceUser",
) => {
  const distinctServiceIds = [...new Set(serviceIds)].filter(Boolean);

  for (const serviceId of distinctServiceIds) {
    const enrollment = await api<ServiceEnrollmentDto>(
      `/api/parties/${partyId}/services`,
      {
        method: "POST",
        body: {
          serviceId,
          enrollmentStatus: "Active",
          memberType,
          memberTypes: [memberType],
          source,
        },
      },
    );

    const serviceRoleIds = selectedServiceRoles[serviceId] ?? [];
    if (serviceRoleIds.length > 0) {
      await api(
        `/api/service-enrollments/${enrollment.serviceEnrollmentId}/roles`,
        {
          method: "PUT",
          body: { serviceRoleIds },
        },
      );
    }
  }
};

const EMPTY_SERVICE_IDS: number[] = [];
const EMPTY_SERVICE_ROLES: ServiceRoleSelections = {};

export default function ServiceEnrollmentCard({
  partyId,
  tenantId,
  selectedServices = EMPTY_SERVICE_IDS,
  onSelectedServicesChange,
  selectedServiceRoles = EMPTY_SERVICE_ROLES,
  onSelectedServiceRolesChange,
  registrationServiceId = null,
  registrationServiceName,
  registrationMemberType = "ServiceUser",
  readonly = false,
}: ServiceEnrollmentCardProps) {
  const [enrollments, setEnrollments] = useState<ServiceEnrollmentDto[]>([]);
  const [services, setServices] = useState<ServiceDto[]>([]);
  const [serviceGroups, setServiceGroups] = useState<ServiceGroupDto[]>([]);
  const [draftServiceRoles, setDraftServiceRoles] = useState<
    Record<number, ServiceRoleDto[]>
  >({});
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedServiceGroupId, setSelectedServiceGroupId] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [editingEnrollmentId, setEditingEnrollmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const hasPersistedParty = Boolean(partyId && partyId > 0);

  const lockedServiceIds = useMemo(
    () => (registrationServiceId ? [registrationServiceId] : []),
    [registrationServiceId],
  );

  const draftSelectedServiceIds = useMemo(
    () => [...new Set([...selectedServices, ...lockedServiceIds])],
    [lockedServiceIds, selectedServices],
  );

  const activeEnrollments = useMemo(
    () =>
      enrollments.filter(
        (enrollment) => enrollment.enrollmentStatus === "Active",
      ),
    [enrollments],
  );

  const availableServices = useMemo(
    () =>
      services.filter(
        (service) =>
          service.isActive &&
          !activeEnrollments.some(
            (enrollment) => enrollment.serviceId === service.serviceId,
          ),
      ),
    [activeEnrollments, services],
  );

  const activeServiceGroups = useMemo(
    () => serviceGroups.filter((group) => group.isActive),
    [serviceGroups],
  );

  const draftSelectedServices = useMemo(
    () =>
      services.filter((service) =>
        draftSelectedServiceIds.includes(service.serviceId),
      ),
    [draftSelectedServiceIds, services],
  );

  const loadEnrollments = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api<ServiceEnrollmentDto[]>(
        `/api/parties/${partyId}/services`,
      );
      setEnrollments(result ?? []);
    } catch (error) {
      console.error("Failed to load service enrollments", error);
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  }, [partyId]);

  const loadServices = useCallback(async () => {
    try {
      const result = await api<ServiceDto[]>(`/api/services?tenantId=${tenantId}`);
      setServices(result ?? []);
    } catch (error) {
      console.error("Failed to load services", error);
      setServices([]);
    }
  }, [tenantId]);

  const loadServiceGroups = useCallback(async (serviceId: number) => {
    try {
      const result = await api<ServiceGroupDto[]>(
        `/api/services/${serviceId}/groups?includeInactive=false`,
      );
      setServiceGroups(result ?? []);
    } catch (error) {
      console.error("Failed to load service groups", error);
      setServiceGroups([]);
    }
  }, []);

  useEffect(() => {
    if (!hasPersistedParty) return;

    loadEnrollments();
  }, [hasPersistedParty, loadEnrollments]);

  useEffect(() => {
    if (!tenantId) return;

    loadServices();
  }, [loadServices, tenantId]);

  useEffect(() => {
    const serviceId = Number(selectedServiceId);
    setSelectedServiceGroupId("");

    if (!serviceId) {
      setServiceGroups([]);
      return;
    }

    loadServiceGroups(serviceId);
  }, [loadServiceGroups, selectedServiceId]);

  useEffect(() => {
    if (!registrationServiceId || hasPersistedParty) return;
    if (selectedServices.includes(registrationServiceId)) return;

    onSelectedServicesChange?.([
      ...selectedServices,
      registrationServiceId,
    ]);
  }, [
    hasPersistedParty,
    onSelectedServicesChange,
    registrationServiceId,
    selectedServices,
  ]);

  useEffect(() => {
    if (
      !onSelectedServiceRolesChange ||
      hasPersistedParty ||
      draftSelectedServiceIds.length === 0
    ) {
      setDraftServiceRoles({});
      return;
    }

    let cancelled = false;

    Promise.all(
      draftSelectedServiceIds.map(async (serviceId) => {
        const roles = await api<ServiceRoleDto[]>(
          `/api/services/${serviceId}/roles`,
          { suppressLoading: true, notifyError: false },
        );
        return [
          serviceId,
          (roles ?? []).filter((role) => role.isActive),
        ] as const;
      }),
    )
      .then((entries) => {
        if (!cancelled) setDraftServiceRoles(Object.fromEntries(entries));
      })
      .catch((error) => {
        console.error("Failed to load service roles", error);
        if (!cancelled) setDraftServiceRoles({});
      });

    return () => {
      cancelled = true;
    };
  }, [
    draftSelectedServiceIds,
    hasPersistedParty,
    onSelectedServiceRolesChange,
  ]);

  const addService = async () => {
    const serviceId = Number(selectedServiceId);
    if (!serviceId) return;

    try {
      setSaving(true);
      await api<ServiceEnrollmentDto>(`/api/parties/${partyId}/services`, {
        method: "POST",
        body: {
          serviceId,
          serviceGroupId: selectedServiceGroupId
            ? Number(selectedServiceGroupId)
            : null,
          enrollmentStatus: "Active",
          memberType: registrationMemberType,
          memberTypes: [registrationMemberType],
          source: "Manual",
        },
      });

      notify.success("Service added.");
      setSelectedServiceId("");
      setSelectedServiceGroupId("");
      setServiceGroups([]);
      setIsAdding(false);
      await loadEnrollments();
    } catch (error) {
      console.error("Failed to add service enrollment", error);
      notify.error("Failed to add service.");
    } finally {
      setSaving(false);
    }
  };

  const removeEnrollment = async (serviceEnrollmentId: number) => {
    try {
      setSaving(true);
      await api<void>(`/api/service-enrollments/${serviceEnrollmentId}`, {
        method: "DELETE",
      });

      notify.success("Service removed.");
      await loadEnrollments();
    } catch (error) {
      console.error("Failed to remove service enrollment", error);
      notify.error("Failed to remove service.");
    } finally {
      setSaving(false);
    }
  };

  const toggleDraftService = (serviceId: number, checked: boolean) => {
    if (readonly || lockedServiceIds.includes(serviceId)) return;

    const next = checked
      ? [...draftSelectedServiceIds, serviceId]
      : draftSelectedServiceIds.filter((id) => id !== serviceId);

    onSelectedServicesChange?.([...new Set(next)]);

    if (!checked && selectedServiceRoles[serviceId]) {
      const nextRoles = { ...selectedServiceRoles };
      delete nextRoles[serviceId];
      onSelectedServiceRolesChange?.(nextRoles);
    }
  };

  const toggleDraftRole = (
    serviceId: number,
    serviceRoleId: number,
    checked: boolean,
  ) => {
    if (readonly) return;

    const current = selectedServiceRoles[serviceId] ?? [];
    const next = checked
      ? [...current, serviceRoleId]
      : current.filter((id) => id !== serviceRoleId);

    onSelectedServiceRolesChange?.({
      ...selectedServiceRoles,
      [serviceId]: [...new Set(next)],
    });
  };

  const renderDraftRoles = (serviceId: number) => {
    if (!onSelectedServiceRolesChange) return null;
    if (registrationMemberType !== "Volunteer" && registrationMemberType !== "Staff") {
      return null;
    }

    const roles = draftServiceRoles[serviceId] ?? [];
    if (roles.length === 0) return null;

    return (
      <div className="service-enrollment-roles-section">
        <div className="service-enrollment-roles-title">Roles</div>
        <div className="field-hint" style={{ marginBottom: 8 }}>
          Select the role or roles this person will hold for this service.
        </div>
        <div className="option-group">
          {roles.map((role) => (
            <label key={role.serviceRoleId} className="option-item">
              <input
                type="checkbox"
                checked={(selectedServiceRoles[serviceId] ?? []).includes(
                  role.serviceRoleId,
                )}
                disabled={readonly}
                onChange={(event) =>
                  toggleDraftRole(
                    serviceId,
                    role.serviceRoleId,
                    event.target.checked,
                  )
                }
              />
              {role.name}
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderDraftEnrollment = (serviceId: number, serviceName: string) => (
    <div className="service-enrollment-draft-item">
      <div className="service-enrollment-draft-summary">
        <div className="service-enrollment-draft-summary-item service-enrollment-draft-summary-item--service">
          <strong title={serviceName}>{serviceName}</strong>
        </div>
        <div className="service-enrollment-draft-summary-item">
          <strong>{memberTypeLabel(registrationMemberType)}</strong>
        </div>
      </div>
      {renderDraftRoles(serviceId)}
    </div>
  );

  const renderDraftSelection = () => (
    <>
      {registrationServiceId && (
        <div className="service-enrollment-draft">
          {registrationServiceName ? (
            renderDraftEnrollment(registrationServiceId, registrationServiceName)
          ) : draftSelectedServices.length > 0 ? (
            draftSelectedServices.map((service) => (
              <React.Fragment key={service.serviceId}>
                {renderDraftEnrollment(service.serviceId, service.name)}
              </React.Fragment>
            ))
          ) : (
            <div className="field-hint">Loading selected service...</div>
          )}
        </div>
      )}

      {!registrationServiceId && readonly && draftSelectedServices.length === 0 && (
        <div className="field-hint">No services selected.</div>
      )}

      {!registrationServiceId && (
        <>
          <div className="option-group">
            {services.map((service) => (
              <div key={service.serviceId} className="service-enrollment-draft-choice">
              <label className="option-item">
                <input
                  type="checkbox"
                  checked={draftSelectedServiceIds.includes(service.serviceId)}
                  disabled={readonly}
                  onChange={(event) =>
                    toggleDraftService(service.serviceId, event.target.checked)
                  }
                />
                {service.name}
              </label>
              {draftSelectedServiceIds.includes(service.serviceId) && renderDraftRoles(service.serviceId)}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="card service-enrollment-card">
      <div className="card-header party-card-header">
        <div className="card-header-row party-card-header-row">
        <div className="party-card-title-group">
          <div className="card-title">Service Enrollment</div>
        </div>

        {hasPersistedParty && tenantId && !readonly && (
          <button
            type="button"
            className="link-button link-button-with-icon card-header-link-action"
            onClick={() => setIsAdding((value) => !value)}
            disabled={saving}
          >
            <AddIcon fontSize="small" />
            Add enrollment
          </button>
        )}
        </div>
      </div>

      {!hasPersistedParty && renderDraftSelection()}

      {hasPersistedParty && isAdding && (
        <div className="form-row service-enrollment-add-form">
          <div className="form-group">
            <label className="form-label">Service</label>
            <select
              className="form-input"
              value={selectedServiceId}
              onChange={(event) => setSelectedServiceId(event.target.value)}
              disabled={saving}
            >
              <option value="">Select active service</option>
              {availableServices.map((service) => (
                <option key={service.serviceId} value={service.serviceId}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>
          {activeServiceGroups.length > 0 && (
            <div className="form-group">
              <label className="form-label">Group / Class</label>
              <select
                className="form-input"
                value={selectedServiceGroupId}
                onChange={(event) =>
                  setSelectedServiceGroupId(event.target.value)
                }
                disabled={saving}
              >
                <option value="">No group/class</option>
                {activeServiceGroups.map((group) => (
                  <option
                    key={group.serviceGroupId}
                    value={group.serviceGroupId}
                  >
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="button-row button-row-left" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="action-button compact"
              onClick={addService}
              disabled={!selectedServiceId || saving}
            >
              Save
            </button>
            <button
              type="button"
              className="action-button compact secondary"
              onClick={() => {
                setSelectedServiceId("");
                setSelectedServiceGroupId("");
                setServiceGroups([]);
                setIsAdding(false);
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {hasPersistedParty && loading && (
        <div className="field-hint">Loading service enrollments...</div>
      )}

      {hasPersistedParty && !loading && activeEnrollments.length === 0 && (
        <div className="field-hint">No service enrollments.</div>
      )}

      {hasPersistedParty && <div className="option-group registered-service-group">
        {activeEnrollments.map((enrollment) => (
          <div
            key={enrollment.serviceEnrollmentId}
            className="registered-service-item"
          >
            <div className="registered-service-header">
              <div className="registered-service-summary">
                <span className="registered-service-name" title={enrollment.serviceName ?? "Service"}>
                  {enrollment.serviceName ?? "Service"}
                </span>
                <span className="registered-service-meta">
                  {memberTypesLabel(enrollment)}
                  {enrollment.serviceGroupName && ` · ${enrollment.serviceGroupName}`}
                </span>
              </div>
              {!readonly && <div className="registered-service-actions">
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Edit ${enrollment.serviceName ?? "service"}`}
                  title="Edit enrollment"
                  onClick={() => setEditingEnrollmentId(enrollment.serviceEnrollmentId)}
                  disabled={saving}
                >
                  <EditIcon fontSize="small" />
                </button>
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove ${enrollment.serviceName ?? "service"}`}
                title="Delete enrollment"
                onClick={() => removeEnrollment(enrollment.serviceEnrollmentId)}
                disabled={saving || readonly}
              >
                <DeleteIcon fontSize="small" />
              </button>
              </div>}
            </div>

            {editingEnrollmentId === enrollment.serviceEnrollmentId && <ServiceEnrollmentMembershipCard
              serviceEnrollmentId={enrollment.serviceEnrollmentId}
              serviceId={enrollment.serviceId}
              memberTypes={
                enrollment.memberTypes?.length
                  ? enrollment.memberTypes
                  : [enrollment.memberType]
              }
              readonly={readonly}
              initiallyEditing
              onClose={() => setEditingEnrollmentId(null)}
              onSaved={loadEnrollments}
            />}
          </div>
        ))}
      </div>}
    </div>
  );
}
